import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { filter } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import { EmployeeDto } from 'src/app/shared/flight-log-api/employees/employee-client.model';
import { EmployeeHttpClient } from 'src/app/shared/flight-log-api/employees/employee-client.service';
import {
  CrewMemberDto,
  CrewRole,
  crewRoleDisplay,
} from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { SelectItem, toSelectionList } from 'src/util/ui/forms';
import { UserViewModel } from 'src/view-models/user-view-model';

@Component({
  selector: 'fl-flight-crew-member-select',
  templateUrl: './flight-crew-employee-select.component.html',
  styleUrls: ['./flight-crew-employee-select.component.scss'],
})
export class FlightCrewMemberSelectComponent implements OnInit {
  @Input()
  public flightCrew: FormArray;
  @Output()
  public crewSelected = new EventEmitter<EmployeeDto>();
  @Input()
  public filter: SelectItem[];
  @Output()
  public filterChanged: EventEmitter<SelectItem[]>;

  public form: FormGroup;
  public options$: Observable<SelectItem[]>;
  public isPilotSelected$: Observable<EmployeeDto | undefined | null>;
  public employeeSelected$: Observable<EmployeeDto>;
  public searches$ = new Subject<string>();
  public componentDestruction$: Subject<any> = new Subject();
  public filteredEmployees: EmployeeDto[];
  public duplicateSelected: boolean;

  get employeeSelected(): AbstractControl {
    // tslint:disable-next-line: no-non-null-assertion
    return this.form.get('employeeSelected')!;
  }

  get pilotInCommand(): AbstractControl {
    // tslint:disable-next-line: no-non-null-assertion
    return this.form.get('pilotInCommand')!;
  }

  get flightCrewList(): CrewMemberDto[] {
    return this.flightCrew.value;
  }

  constructor(@Inject(USER) private user: UserViewModel, private _emp: EmployeeHttpClient) {}

  public ngOnInit() {
    this.form = new FormGroup({
      employeeSelected: new FormControl('', Validators.required),
      pilotInCommand: new FormControl(true),
    });

    // setup observables
    const employeeSearchResults$ = this.searches$.pipe(
      switchMap((searchTerm) =>
        this._emp.getAll({
          $top: 100,
          companyId: this.user.companycode,
          searchTerm,
        }),
      ),
      map((emps) => filter(emps, this.hasRole)),
    );

    this.isPilotSelected$ = this.employeeSelected.valueChanges.pipe(
      map((empId) =>
        this.isValid(empId)
          ? this.filteredEmployees.find((fc) => fc.id === empId && fc.role === 'PilotInCommand')
          : null,
      ),
    );

    this.options$ = employeeSearchResults$.pipe(
      map(
        toSelectionList<EmployeeDto>(
          (emp) =>
            `${emp.firstName} ${emp.lastName} (${crewRoleDisplay(emp.role)} - ${emp.employeeId})`,
          'id',
        ),
      ),
    );

    // apply observable logic
    employeeSearchResults$
      .pipe(takeUntil(this.componentDestruction$))
      .subscribe((emps) => (this.filteredEmployees = emps));

    this.employeeSelected.valueChanges
      .pipe(takeUntil(this.componentDestruction$))
      .subscribe((empId) => {
        this.duplicateSelected = this.flightCrewList.find((c) => c.employeeId === empId) != null;
      });
  }

  public onSearch(searchTerm: string) {
    return this.searches$.next(searchTerm);
  }

  public addEmployeeToCrew() {
    if (this.employeeSelected.value != null) {
      // tslint:disable-next-line: no-non-null-assertion
      const emp = this.filteredEmployees.find((e) => e.id === this.employeeSelected.value)!;
      // change role if co-pilot.
      emp.role = this.pilotInCommand.value == null ? CrewRole.CoPilot : emp.role;
      this.crewSelected.emit(emp);
      this.employeeSelected.setValue('');
      this.pilotInCommand.setValue(true);
    }
  }

  public hasRole(emp: EmployeeDto) {
    return emp.role === 'PilotInCommand' || emp.role === 'CoPilot' || emp.role === 'Clinical';
  }

  public isValid(empId: any) {
    return empId !== undefined && empId != null && empId !== '';
  }
}
